import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUsageLimits } from '@/hooks/useBillingAndUsage';
import { toFriendlyLimitError } from '@/lib/limitErrors';
import { toast } from 'sonner';
import { contentSubmissionRepository } from '../repositories/contentSubmission.repository';
import { contentMetricsRepository } from '../repositories/contentMetrics.repository';
import { contentKeys } from '../services/content.keys';
import { deliverableKeys } from '@/domains/deliverables/services/deliverable.keys';

export interface ScrapeProgress {
  current: number;
  total: number;
}

export const useScrapeCampaignDeliverables = (
  campaignId: string,
  targetOrgId?: string
) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = targetOrgId || currentOrganization?.id || '';
  const { isAtLimit } = useUsageLimits();

  const [isScraping, setIsScraping] = useState<boolean>(false);
  const [progress, setProgress] = useState<ScrapeProgress | null>(null);

  const scrapeCampaign = useCallback(async () => {
    if (!campaignId || !orgId) {
      toast.error('Campaign or organization identifier missing.');
      return;
    }

    if (isScraping) return;

    if (isAtLimit('deliverables')) {
      toast.error('Monthly deliverable limit reached', {
        description: 'Please upgrade your plan to fetch more deliverable metrics.',
      });
      return;
    }

    setIsScraping(true);
    setProgress(null);

    try {
      // 1. Fetch all posts for this campaign
      const posts = await contentSubmissionRepository.getPostsByCampaignId(orgId, campaignId);

      if (posts.length === 0) {
        toast.info('No deliverables found in this campaign.');
        setIsScraping(false);
        return;
      }

      // 2. Filter for deliverables that can be scraped (Instagram URLs)
      const scrapablePosts = posts.filter(
        (post) => post.post_url && post.post_url.toLowerCase().includes('instagram.com')
      );

      if (scrapablePosts.length === 0) {
        toast.info('No Instagram deliverables found to fetch metrics for.');
        setIsScraping(false);
        return;
      }

      setProgress({ current: 0, total: scrapablePosts.length });

      let succeeded = 0;
      let cooldown = 0;
      let failed = 0;
      const errorMessages: string[] = [];

      // 3. Process deliverables in batches of 3 for optimal concurrency
      const BATCH_SIZE = 3;
      for (let i = 0; i < scrapablePosts.length; i += BATCH_SIZE) {
        const batch = scrapablePosts.slice(i, i + BATCH_SIZE);

        await Promise.allSettled(
          batch.map(async (post) => {
            try {
              const res = await contentMetricsRepository.triggerPostScrape(post.id);

              if (res?.error) {
                if (res.error === 'cooldown') {
                  cooldown++;
                } else {
                  failed++;
                  errorMessages.push((res as any).details || res.error);
                }
              } else if (res?.status === 'success' || !res?.error) {
                succeeded++;
              } else if (res?.status === 'failed') {
                failed++;
                if (res.message) errorMessages.push(res.message);
              } else {
                succeeded++;
              }
            } catch (err: unknown) {
              failed++;
              const msg = err instanceof Error ? err.message : String(err);
              errorMessages.push(msg);
            }
          })
        );

        const currentCount = Math.min(i + BATCH_SIZE, scrapablePosts.length);
        setProgress({ current: currentCount, total: scrapablePosts.length });
      }

      // 4. Invalidate all relevant queries to update UI immediately
      queryClient.invalidateQueries({ queryKey: deliverableKeys.campaignAggregates(orgId, campaignId) });
      queryClient.invalidateQueries({ queryKey: deliverableKeys.legacyCampaignAggregates(campaignId) });
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      queryClient.invalidateQueries({ queryKey: contentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['content-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-budget-calculations', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-influencers', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['top-performing-posts'] });
      queryClient.invalidateQueries({ queryKey: ['metrics-history'] });
      queryClient.invalidateQueries({ queryKey: ['usage-stats', orgId] });
      queryClient.invalidateQueries({ queryKey: ['usage-limits', orgId] });

      // 5. Toast summary feedback
      if (succeeded > 0) {
        const msg =
          cooldown > 0
            ? `Metrics fetched for ${succeeded} deliverable${succeeded > 1 ? 's' : ''} (${cooldown} in cooldown).`
            : `Metrics fetched for ${succeeded} deliverable${succeeded > 1 ? 's' : ''}.`;
        toast.success(msg);
      } else if (cooldown > 0 && failed === 0) {
        toast.info(
          `All ${cooldown} deliverable${cooldown > 1 ? 's are' : ' is'} in cooldown (15m). Please wait before fetching again.`
        );
      } else if (failed > 0) {
        const firstError = errorMessages[0] || "Couldn't fetch metrics for deliverables.";
        const friendly = toFriendlyLimitError(firstError, firstError);
        toast.error("Couldn't fetch metrics", { description: friendly });
      }
    } catch (err: unknown) {
      console.error('[useScrapeCampaignDeliverables] Scrape error:', err);
      const fallback =
        (err as { message?: string })?.message ||
        (err as { details?: string })?.details ||
        (err instanceof Error
          ? err.message
          : 'An unexpected error occurred while fetching campaign deliverable metrics.');
      const friendly = toFriendlyLimitError(err, fallback);
      toast.error('Fetch metrics failed', { description: friendly });
    } finally {
      setIsScraping(false);
      setProgress(null);
    }
  }, [campaignId, orgId, isScraping, isAtLimit, queryClient]);

  return {
    scrapeCampaign,
    isScraping,
    progress,
  };
};
